import { supabase } from './supabase'
import { findEmployeeByUserProfileId } from '../api/team'
import {
  jobStartGpsPatch,
  type GeoCaptureResult,
} from './geolocation'
import {
  FieldGpsError,
  gateGpsOnlyAction,
  gateJobSiteAction,
} from './fieldGpsGate'
import { hoursForTimeEntry } from './jobActualHours'
import { cacheEmployeeId, readCachedEmployeeId } from './offlineMyDayCache'

export interface OpenInProgressJob {
  id: string
  job_number: string
  title: string
}

/**
 * Assignment-aware lookup for an employee's unfinished in_progress job
 * (primary assignee, crew membership, or active job_assignments).
 * Uses the DB RPC so large assignment histories (e.g. rbctest GPS seed) stay
 * within the 8s authenticated statement_timeout.
 */
export async function findOpenInProgressJobForEmployee(
  employeeId: string
): Promise<OpenInProgressJob | null> {
  const { data, error } = await supabase.rpc('employee_has_other_in_progress_job', {
    p_employee_id: employeeId,
    p_exclude_job_id: null,
  })
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as
    | { id: string; job_number?: string | null; title?: string | null }
    | null
    | undefined
  if (!row?.id) return null
  return {
    id: row.id,
    job_number: row.job_number || '',
    title: row.title || '',
  }
}

/**
 * Tenant setting `require_finish_job_before_start_next` (OFF by default).
 * Re-enable later: set tenants.settings.require_finish_job_before_start_next = true
 */
export async function tenantRequiresFinishJobBeforeStart(): Promise<boolean> {
  const { data, error } = await supabase.rpc('tenant_requires_finish_job_before_start')
  if (error) return false
  return data === true
}

/** Block starting/resuming a different job while another assigned job is still in_progress. */
export async function assertCanStartOrResumeJob(
  employeeId: string,
  jobId: string
): Promise<void> {
  if (!(await tenantRequiresFinishJobBeforeStart())) return
  const open = await findOpenInProgressJobForEmployee(employeeId)
  if (open && open.id !== jobId) {
    const display = open.job_number
      ? open.job_number.match(/^JOB-/i)
        ? open.job_number
        : `JOB-${open.job_number}`
      : open.title || 'your current job'
    throw new Error(`Finish ${display} before starting another.`)
  }
}

/**
 * Resolves the employees.id linked to a logged-in user. Prefers the
 * get_employee_for_user RPC (see 20260717_time_tracking.sql); falls back to a
 * direct employees lookup if the RPC isn't available yet (e.g. migration pending).
 */
export async function resolveEmployeeIdForUser(userId: string): Promise<string | null> {
  const cached = readCachedEmployeeId(userId)

  try {
    const { data, error } = await supabase.rpc('get_employee_for_user', { p_user_id: userId })
    if (!error && data) {
      cacheEmployeeId(userId, data as string)
      return data as string
    }
  } catch {
    // fall through to direct lookup / offline cache
  }

  try {
    const employee = await findEmployeeByUserProfileId(userId)
    if (employee?.id) {
      cacheEmployeeId(userId, employee.id)
      return employee.id
    }
  } catch {
    return cached
  }

  return cached
}

export type TimeEntryType = 'day' | 'job' | 'travel'

export interface ActiveTimeEntry {
  id: string
  job_id: string | null
  employee_id: string
  clock_in_time: string
  entry_type?: TimeEntryType | null
  notes?: string | null
}

/** Dual-track clock: day stays open while job or travel segments run. */
export interface EmployeeClockState {
  dayEntry: ActiveTimeEntry | null
  jobEntry: ActiveTimeEntry | null
  travelEntry: ActiveTimeEntry | null
}

const ENTRY_SELECT = 'id, job_id, employee_id, clock_in_time, entry_type, notes'

/** Re-export for callers that already import clock helpers. */
export { captureGeolocation, warnIfGpsDenied } from './geolocation'
export type { GeoCaptureResult, GeoPosition } from './geolocation'
export { FieldGpsError } from './fieldGpsGate'

function requirePosition(
  geo: GeoCaptureResult,
  actionLabel: string
): NonNullable<GeoCaptureResult['position']> {
  if (!geo.position) {
    throw new FieldGpsError(
      `${actionLabel}: Location access is required. Enable GPS/location permission and try again.`,
      'gps_required',
      { geo }
    )
  }
  return geo.position
}

export async function fetchEmployeeClockState(employeeId: string): Promise<EmployeeClockState> {
  const { data, error } = await supabase
    .from('time_entries')
    .select(ENTRY_SELECT)
    .eq('employee_id', employeeId)
    .is('clock_out_time', null)
    .order('clock_in_time', { ascending: false })

  if (error) throw error

  const rows = (data || []) as ActiveTimeEntry[]
  let dayEntry: ActiveTimeEntry | null = null
  let jobEntry: ActiveTimeEntry | null = null
  let travelEntry: ActiveTimeEntry | null = null

  for (const row of rows) {
    const t = row.entry_type
    if (t === 'day' && !dayEntry) dayEntry = row
    else if (t === 'job' && !jobEntry) jobEntry = row
    else if (t === 'travel' && !travelEntry) travelEntry = row
    else if (!t) {
      // Legacy rows without entry_type
      if (row.job_id && !jobEntry) jobEntry = row
      else if (!row.job_id && !dayEntry) dayEntry = { ...row, entry_type: 'day' }
    }
  }

  return { dayEntry, jobEntry, travelEntry }
}

/**
 * Close a leftover open job punch without prompting for GPS.
 * Used when the job is already finished (pending_review / under_review) but the
 * segment was never clocked out — so other Starts are not blocked.
 */
export async function closeStaleJobTimeEntry(entry: ActiveTimeEntry): Promise<void> {
  const totalHours = hoursForTimeEntry({
    clock_in_time: entry.clock_in_time,
    clock_out_time: null,
    total_hours: null,
    entry_type: entry.entry_type || 'job',
  })
  const { error } = await supabase
    .from('time_entries')
    .update({
      clock_out_time: new Date().toISOString(),
      total_hours: totalHours,
    })
    .eq('id', entry.id)
    .is('clock_out_time', null)
  if (error) throw error
}

/** Prefer open job, then travel, then day — for simple “am I on the clock?” UIs. */
export async function fetchActiveTimeEntry(employeeId: string): Promise<ActiveTimeEntry | null> {
  const state = await fetchEmployeeClockState(employeeId)
  return state.jobEntry || state.travelEntry || state.dayEntry
}

async function insertEntry(params: {
  employeeId: string
  jobId?: string | null
  entryType: TimeEntryType
  notes?: string | null
  clockInTime?: string
  geo: GeoCaptureResult
  distanceFt?: number | null
}): Promise<{ entry: ActiveTimeEntry; geo: GeoCaptureResult }> {
  const position = requirePosition(params.geo, 'Clock in')
  const { data, error } = await supabase
    .from('time_entries')
    .insert({
      employee_id: params.employeeId,
      job_id: params.jobId || null,
      entry_type: params.entryType,
      clock_in_time: params.clockInTime || new Date().toISOString(),
      notes: params.notes || null,
      clock_in_latitude: position.latitude,
      clock_in_longitude: position.longitude,
      clock_in_accuracy_m: position.accuracy,
      clock_in_distance_ft: params.distanceFt ?? null,
    })
    .select(ENTRY_SELECT)
    .single()

  if (error) throw error
  return { entry: data as ActiveTimeEntry, geo: params.geo }
}

/** Human-readable day-punch note when associating attendance with a job (job_id stays null). */
export function dayPunchInNotesForJob(job: {
  job_number?: string | null
  title?: string | null
}): string {
  const raw = (job.job_number || '').trim()
  const num = raw
    ? raw.match(/^JOB-/i)
      ? raw
      : `JOB-${raw}`
    : ''
  if (num) return `Punch in for ${num}`
  const title = (job.title || '').trim()
  return title ? `Punch in for ${title}` : 'Punch in'
}

export async function clockIn(params: {
  employeeId: string
  jobId?: string | null
  notes?: string | null
  entryType?: TimeEntryType
  /**
   * Apply the same property geofence as Start, without opening a job segment.
   * Used by Home Punch In when a job is selected. Day rows keep job_id null.
   */
  geofenceJobId?: string | null
}): Promise<{ entry: ActiveTimeEntry; geo: GeoCaptureResult }> {
  const { employeeId, notes } = params
  const requestedJobId = params.jobId || null
  const entryType = params.entryType || (requestedJobId ? 'job' : 'day')
  // Day punches must not set job_id: billing/legacy treat job_id as a job segment.
  const jobId = entryType === 'day' ? null : requestedJobId

  if (entryType === 'job' && jobId) {
    await assertCanStartOrResumeJob(employeeId, jobId)
  }

  const fenceJobId =
    entryType === 'job' && jobId ? jobId : entryType === 'day' ? params.geofenceJobId || null : null
  const actionLabel = entryType === 'day' ? 'Punch in' : 'Clock in'

  let geo: GeoCaptureResult
  let distanceFt: number | null = null
  if (fenceJobId) {
    const gated = await gateJobSiteAction({
      jobId: fenceJobId,
      actionLabel,
      attemptType: 'job_start',
    })
    geo = gated.geo
    distanceFt = gated.distanceFt
  } else {
    geo = await gateGpsOnlyAction(actionLabel)
  }

  return insertEntry({ employeeId, jobId, entryType, notes, geo, distanceFt })
}

export async function clockOut(
  entryId: string,
  notes?: string | null,
  asOf?: string | Date | null,
  geoIn?: GeoCaptureResult,
  distanceFtIn?: number | null
): Promise<{ totalHours: number; geo: GeoCaptureResult }> {
  const { data: entry, error: fetchError } = await supabase
    .from('time_entries')
    .select('clock_in_time, employee_id, job_id, entry_type')
    .eq('id', entryId)
    .single()
  if (fetchError) throw fetchError

  const geo = geoIn?.position ? geoIn : await gateGpsOnlyAction('Clock out')
  const position = requirePosition(geo, 'Clock out')
  const clockOutTime = asOf ? new Date(asOf) : new Date()
  const clockInTime = new Date(entry.clock_in_time)
  const totalHours =
    Math.round(((clockOutTime.getTime() - clockInTime.getTime()) / (1000 * 60 * 60)) * 100) / 100

  const { error } = await supabase
    .from('time_entries')
    .update({
      clock_out_time: clockOutTime.toISOString(),
      total_hours: totalHours,
      notes: notes ?? undefined,
      clock_out_latitude: position.latitude,
      clock_out_longitude: position.longitude,
      clock_out_accuracy_m: position.accuracy,
      clock_out_distance_ft: distanceFtIn ?? null,
    })
    .eq('id', entryId)

  if (error) throw error
  return { totalHours, geo }
}

/** Starts a job (status -> in_progress) if it isn't already underway. Stamps start GPS (required). */
export async function startJobIfNeeded(
  jobId: string,
  currentStatus: string,
  geoIn?: GeoCaptureResult,
  employeeId?: string | null
): Promise<GeoCaptureResult> {
  if (currentStatus === 'in_progress' || currentStatus === 'completed') {
    if (geoIn?.position) return geoIn
    return gateGpsOnlyAction('Job start')
  }

  if (employeeId) {
    await assertCanStartOrResumeJob(employeeId, jobId)
  }

  const geo = geoIn?.position
    ? geoIn
    : (
        await gateJobSiteAction({
          jobId,
          actionLabel: 'Job start',
          attemptType: 'job_start',
        })
      ).geo
  const position = requirePosition(geo, 'Job start')
  const { error } = await supabase
    .from('jobs')
    .update({
      status: 'in_progress',
      actual_start_time: new Date().toISOString(),
      ...jobStartGpsPatch(position),
    })
    .eq('id', jobId)
  if (error) throw error
  return geo
}

/**
 * Least-clicks Start: ensure day punch is open, end travel if active, open job segment, mark job in_progress.
 */
export async function startJobWorkflow(params: {
  employeeId: string
  jobId: string
  currentStatus: string
}): Promise<{ state: EmployeeClockState; geo: GeoCaptureResult }> {
  const { employeeId, jobId, currentStatus } = params
  await assertCanStartOrResumeJob(employeeId, jobId)
  const state = await fetchEmployeeClockState(employeeId)

  const gated = await gateJobSiteAction({
    jobId,
    actionLabel: currentStatus === 'in_progress' ? 'Job resume' : 'Job start',
    attemptType: 'job_start',
  })
  const geo = gated.geo
  const distanceFt = gated.distanceFt

  if (state.jobEntry) {
    if (state.jobEntry.job_id === jobId) {
      await startJobIfNeeded(jobId, currentStatus, geo, employeeId)
      return { state: await fetchEmployeeClockState(employeeId), geo }
    }
    if (await tenantRequiresFinishJobBeforeStart()) {
      const open = await findOpenInProgressJobForEmployee(employeeId)
      if (open && open.id === state.jobEntry.job_id) {
        throw new Error('Finish or complete your current job before starting another.')
      }
    }
    // Leftover punch, or another in-progress job when finish-before-start is OFF.
    await clockOut(state.jobEntry.id, null, null, geo, distanceFt)
  }

  if (!state.dayEntry) {
    await insertEntry({ employeeId, entryType: 'day', geo, distanceFt })
  }

  if (state.travelEntry) {
    await clockOut(state.travelEntry.id, null, null, geo, distanceFt)
  }

  await insertEntry({ employeeId, jobId, entryType: 'job', geo, distanceFt })
  await startJobIfNeeded(jobId, currentStatus, geo, employeeId)
  return { state: await fetchEmployeeClockState(employeeId), geo }
}

/** Close the open job segment and enter Travel (day punch stays open). */
export async function enterTravelAfterJob(params: {
  employeeId: string
  jobId: string
  geo?: GeoCaptureResult
  distanceFt?: number | null
}): Promise<{ state: EmployeeClockState; geo: GeoCaptureResult }> {
  const { employeeId, jobId } = params
  const state = await fetchEmployeeClockState(employeeId)
  const geo = params.geo?.position ? params.geo : await gateGpsOnlyAction('Travel')
  const distanceFt = params.distanceFt ?? null
  requirePosition(geo, 'Travel')

  if (state.jobEntry && (state.jobEntry.job_id === jobId || !jobId)) {
    await clockOut(state.jobEntry.id, null, null, geo, distanceFt)
  } else if (state.jobEntry) {
    // Completing a job that isn't the open segment — still ensure no open job before travel
    await clockOut(state.jobEntry.id, null, null, geo, distanceFt)
  }

  const refreshed = await fetchEmployeeClockState(employeeId)
  if (!refreshed.dayEntry) {
    // Legacy path: no day entry — open day so travel has a parent day clock
    await insertEntry({ employeeId, entryType: 'day', geo, distanceFt })
  }
  if (!refreshed.travelEntry) {
    await insertEntry({ employeeId, entryType: 'travel', geo, distanceFt })
  }
  return { state: await fetchEmployeeClockState(employeeId), geo }
}

/**
 * Punch out for the day. When `asOf` is set (travel idle / last-job flow), day and travel
 * close at that timestamp — typically the previous job's completion (= travel clock-in).
 */
export async function punchOutForDay(params: {
  employeeId: string
  asOf?: string | null
  notes?: string | null
}): Promise<{ geo: GeoCaptureResult }> {
  const { employeeId, asOf, notes } = params
  const state = await fetchEmployeeClockState(employeeId)
  const geo = await gateGpsOnlyAction('Clock out')
  const outAt =
    asOf ||
    (state.travelEntry ? state.travelEntry.clock_in_time : null) ||
    new Date().toISOString()

  if (state.jobEntry) {
    await clockOut(state.jobEntry.id, notes, asOf ? outAt : undefined, geo)
  }
  if (state.travelEntry) {
    // Zero out travel when backdating to previous job completion
    await clockOut(
      state.travelEntry.id,
      notes,
      asOf ? state.travelEntry.clock_in_time : undefined,
      geo
    )
  }
  const day = state.dayEntry || (await fetchEmployeeClockState(employeeId)).dayEntry
  if (day) {
    await clockOut(day.id, notes, asOf ? outAt : undefined, geo)
  }
  return { geo }
}

export type DayPromptType = 'idle_30min' | 'last_job_done'
export type DayPromptResponse = 'done_for_day' | 'still_working' | 'no_response'

export async function logDayPrompt(params: {
  employeeId: string
  promptType: DayPromptType
  relatedJobId?: string | null
  dayEntryId?: string | null
}): Promise<string | null> {
  const { data, error } = await supabase
    .from('time_day_prompts')
    .insert({
      employee_id: params.employeeId,
      prompt_type: params.promptType,
      related_job_id: params.relatedJobId || null,
      day_entry_id: params.dayEntryId || null,
      response: 'no_response',
    })
    .select('id')
    .single()
  if (error) {
    // Non-blocking audit — don't fail the workflow
    return null
  }
  return (data?.id as string) || null
}

export async function respondDayPrompt(params: {
  promptId: string | null
  response: DayPromptResponse
  autoPunchedOut?: boolean
}): Promise<void> {
  if (!params.promptId) return
  const { error } = await supabase
    .from('time_day_prompts')
    .update({
      response: params.response,
      responded_at: new Date().toISOString(),
      auto_punched_out: !!params.autoPunchedOut,
    })
    .eq('id', params.promptId)
  if (error) throw error
}

export function travelMinutesOpen(travelEntry: ActiveTimeEntry | null, now = Date.now()): number {
  if (!travelEntry?.clock_in_time) return 0
  return (now - new Date(travelEntry.clock_in_time).getTime()) / (1000 * 60)
}
